import React, { useState, useEffect } from 'react';
import {
  FolderOpen, X, AlertTriangle, RefreshCw, Users, Activity, List, Settings,
} from 'lucide-react';
import { databasesApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  setDownloadLocation,
  getLocationLabel,
  clearDownloadLocation,
  isFileSystemAccessSupported,
} from '../../utils/downloadLocation';
import { isDebugMode, setDebugMode } from '../../utils/debug';
import {
  DEBUG_HOVER_TOOLS,
  DEBUG_OVERLAY_TOOLS,
  SHOW_DEBUG_KEY,
  CSS_VARS_MODE_KEY,
  syncHoverState,
  syncOverlayTool,
  applyScreenRestrict,
  cleanupAllOverlays,
  notifyDebugChanged,
} from '../../utils/debugOverlay';

// â”€â”€ Constants â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const ZOOM_OPTIONS = ['75%', '80%', '90%', '100%', '110%', '125%', '150%'];
const THEME_OPTIONS = [
  { label: 'Standard',    value: 'standard' },
  { label: 'Dark',        value: 'dark'     },
  { label: 'Dim',         value: 'dim'      },
  { label: 'Sepia',       value: 'sepia'    },
  { label: 'Hi-Contrast', value: 'hc'       },
  { label: 'T1E',         value: 't1e'      },
];
const THEME_CLASSES = ['dawn-theme-dark', 'dawn-theme-dim', 'dawn-theme-sepia', 'dawn-theme-hc', 'dawn-theme-t1e'];
const CONCURRENCY_OPTIONS = [
  { label: '1',         value: '1'         },
  { label: '2',         value: '2'         },
  { label: '3',         value: '3'         },
  { label: 'Unlimited', value: 'unlimited' },
];
const LOCATION_KEYS = [
  { key: 'app-default',    label: 'Application Default',  sub: 'Fallback for all downloads when no specific location is set'  },
  { key: 'local-reports',  label: 'Local Report Exports', sub: 'PDF, Excel, and CSV reports generated on this machine'        },
  { key: 'remote-reports', label: 'Remote Re-downloads',  sub: "Reports re-downloaded from another machine's history"         },
];
const LANGUAGE_OPTIONS = [
  { label: 'English (US)', value: 'en-US' },
  { label: 'English (UK)', value: 'en-GB' },
  { label: 'Spanish',      value: 'es'    },
  { label: 'French',       value: 'fr'    },
  { label: 'German',       value: 'de'    },
];
const TIMEZONE_OPTIONS = [
  'America/New_York', 'America/Chicago', 'America/Denver',
  'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu', 'UTC',
];
const DATE_FORMAT_OPTIONS = [
  { label: 'MM/DD/YYYY', value: 'MM/DD/YYYY' },
  { label: 'DD/MM/YYYY', value: 'DD/MM/YYYY' },
  { label: 'YYYY-MM-DD', value: 'YYYY-MM-DD' },
];
const TIME_FORMAT_OPTIONS = [
  { label: '12-hour', value: '12h' },
  { label: '24-hour', value: '24h' },
];
const NUMBER_FORMAT_OPTIONS = [
  { label: '1,234.56', value: 'en' },
  { label: '1.234,56', value: 'eu' },
];
const SESSION_TIMEOUT_OPTIONS = [
  { label: '15 min',  value: '15'    },
  { label: '30 min',  value: '30'    },
  { label: '1 hour',  value: '60'    },
  { label: '4 hours', value: '240'   },
  { label: 'Never',   value: 'never' },
];
const SUBSCRIPTION_TOGGLES = [
  { key: 'pmd-sub.exports', label: 'Export completions',      sub: 'Notify when a report export finishes',              defaultOn: true  },
  { key: 'pmd-sub.system',  label: 'System announcements',    sub: 'Important notices from the application team',       defaultOn: true  },
  { key: 'pmd-sub.reports', label: 'Report updates',          sub: 'Notify when a shared report is updated',            defaultOn: false },
  { key: 'pmd-sub.digest',  label: 'Activity digest (daily)', sub: 'Daily summary of your activity and exports',        defaultOn: false },
];

// â”€â”€ Helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const applyTheme = (value) => {
  document.documentElement.classList.remove(...THEME_CLASSES);
  if (value && value !== 'standard') document.documentElement.classList.add(`dawn-theme-${value}`);
};

const detectSession = () => {
  const ua = navigator.userAgent;
  let browser = 'Unknown', os = 'Unknown', device = 'PC';
  if      (ua.includes('Edg/'))                            browser = 'Microsoft Edge';
  else if (ua.includes('Chrome/') && !ua.includes('OPR'))  browser = 'Chrome';
  else if (ua.includes('Firefox/'))                        browser = 'Firefox';
  else if (ua.includes('Safari/') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('OPR/') || ua.includes('Opera/'))   browser = 'Opera';
  if      (ua.includes('Windows NT'))  os = 'Windows';
  else if (ua.includes('Mac OS X'))    os = 'macOS';
  else if (ua.includes('Android'))     os = 'Android';
  else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  else if (ua.includes('Linux'))       os = 'Linux';
  if      (ua.includes('Mobile'))  device = 'Mobile';
  else if (ua.includes('Tablet') || ua.includes('iPad'))   device = 'Tablet';
  return { browser, os, device };
};

// â”€â”€ Sub-components â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const SectionHeader = ({ title, underConstruction = false }) => (
  <div className="stui-detail-section-header">
    <span>{title}</span>
    {underConstruction && <span className="stui-badge-construction">Under Construction</span>}
  </div>
);

const ToggleRow = ({ label, sub, checked, onChange, badge = false }) => (
  <div className="stui-row-setting">
    <div className="stui-row-setting-label">
      <div className="stui-label">{badge && <span className="stui-badge-construction">Under Construction</span>}{label}</div>
      {sub && <div className="stui-sublabel">{sub}</div>}
    </div>
    <label className="stui-toggle">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="stui-toggle-slider" />
    </label>
  </div>
);

// â”€â”€ Main page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const PreferencesPage = () => {
  // eslint-disable-next-line no-unused-vars
  const { currentUser } = useAuth();

  const [databases, setDatabases]           = useState([]);
  const [defaultDb, setDefaultDb]           = useState(localStorage.getItem('pmd-selected-db-slug') || '');
  const [zoom, setZoomState]                = useState(localStorage.getItem('dawn.uiZoom') || '100%');
  const [theme, setThemeState]              = useState(localStorage.getItem('dawn.uiTheme') || 'standard');
  const [concurrency, setConcurrencyState]  = useState(localStorage.getItem('activityTracker.concurrencyLimit') || '2');
  const [debugMode, setDebugState]          = useState(isDebugMode());
  const [locationLabels, setLocationLabels] = useState({ 'app-default': null, 'local-reports': null, 'remote-reports': null });

  const [language,     setLanguage]     = useState(localStorage.getItem('pmd-locale.language')     || 'en-US');
  const [dateFormat,   setDateFormat]   = useState(localStorage.getItem('pmd-locale.dateFormat')   || 'MM/DD/YYYY');
  const [timeFormat,   setTimeFormat]   = useState(localStorage.getItem('pmd-locale.timeFormat')   || '12h');
  const [timezone,     setTimezone]     = useState(localStorage.getItem('pmd-locale.timezone')     || 'America/New_York');
  const [numberFormat, setNumberFormat] = useState(localStorage.getItem('pmd-locale.numberFormat') || 'en');

  const [subs, setSubs] = useState(() => {
    const out = {};
    SUBSCRIPTION_TOGGLES.forEach(t => {
      out[t.key] = localStorage.getItem(t.key) !== null ? localStorage.getItem(t.key) === 'true' : t.defaultOn;
    });
    return out;
  });

  const [sessionTimeout,   setSessionTimeout]   = useState(localStorage.getItem('pmd-security.sessionTimeout') || 'never');
  const [screenRestrict,   setScreenRestrict]   = useState(localStorage.getItem('pmd-security.screenRestrict') === 'true');
  const [autofillUsername, setAutofillUsername] = useState(localStorage.getItem('pmd-autofill.username') !== 'false');
  const [autofillForms,    setAutofillForms]    = useState(localStorage.getItem('pmd-autofill.forms') !== 'false');

  const initTools = (list) => {
    const out = {};
    list.forEach(t => { out[t.key] = localStorage.getItem(t.lsKey) === 'true'; });
    return out;
  };
  const [showDebugPanel, setShowDebugPanel] = useState(localStorage.getItem(SHOW_DEBUG_KEY) === 'true');
  const [hoverTools,     setHoverTools]     = useState(() => initTools(DEBUG_HOVER_TOOLS));
  const [overlayTools,   setOverlayTools]   = useState(() => initTools(DEBUG_OVERLAY_TOOLS));
  const [cssVarsMode,    setCssVarsMode]    = useState(localStorage.getItem(CSS_VARS_MODE_KEY) || 'used');

  const [restoreConfirm,  setRestoreConfirm]  = useState(false);

  const fsSupported = isFileSystemAccessSupported();
  const session     = detectSession();

  useEffect(() => {
    databasesApi.getAvailable()
      .then(res => setDatabases(res.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const loadLabels = async () => {
      const [appDef, local, remote] = await Promise.all([
        getLocationLabel('app-default'),
        getLocationLabel('local-reports'),
        getLocationLabel('remote-reports'),
      ]);
      setLocationLabels({ 'app-default': appDef, 'local-reports': local, 'remote-reports': remote });
    };
    loadLabels();
  }, []);

  useEffect(() => {
    Object.entries(initTools(DEBUG_OVERLAY_TOOLS)).forEach(([key, on]) => syncOverlayTool(key, on));
    applyScreenRestrict(localStorage.getItem('pmd-security.screenRestrict') === 'true');
    syncHoverState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleZoom = (value) => {
    setZoomState(value);
    localStorage.setItem('dawn.uiZoom', value);
    document.documentElement.style.zoom = value === '100%' ? '' : value;
  };
  const handleTheme = (value) => {
    setThemeState(value);
    localStorage.setItem('dawn.uiTheme', value);
    applyTheme(value);
    window.dispatchEvent(new Event('dawn-theme-changed'));
  };
  const handleConcurrency = (value) => {
    setConcurrencyState(value);
    localStorage.setItem('activityTracker.concurrencyLimit', value);
  };
  const handleDefaultDb = (slug) => {
    setDefaultDb(slug);
    localStorage.setItem('pmd-selected-db-slug', slug);
    const db = databases.find(d => (d.displayName || d.name)?.toLowerCase() === slug);
    if (db) localStorage.setItem('pmd-selected-database', db.databaseName || db.id);
  };
  const handleDebugToggle = (e) => {
    setDebugState(e.target.checked);
    setDebugMode(e.target.checked);
  };
  const handleBrowse = async (key) => {
    const label = await setDownloadLocation(key);
    if (label) setLocationLabels(prev => ({ ...prev, [key]: label }));
  };
  const handleClearLocation = async (key) => {
    await clearDownloadLocation(key);
    setLocationLabels(prev => ({ ...prev, [key]: null }));
  };
  const handleLocale = (setter, lsKey) => (value) => {
    setter(value);
    localStorage.setItem(lsKey, value);
  };
  const handleSub = (key, checked) => {
    setSubs(prev => ({ ...prev, [key]: checked }));
    localStorage.setItem(key, checked.toString());
  };
  const handleSessionTimeout = (value) => {
    setSessionTimeout(value);
    localStorage.setItem('pmd-security.sessionTimeout', value);
  };
  const handleScreenRestrict = (e) => {
    const on = e.target.checked;
    setScreenRestrict(on);
    localStorage.setItem('pmd-security.screenRestrict', on.toString());
    applyScreenRestrict(on);
  };
  const handleAutofillUsername = (e) => {
    const on = e.target.checked;
    setAutofillUsername(on);
    localStorage.setItem('pmd-autofill.username', on.toString());
  };
  const handleAutofillForms = (e) => {
    const on = e.target.checked;
    setAutofillForms(on);
    localStorage.setItem('pmd-autofill.forms', on.toString());
  };
  const handleShowDebugPanel = (e) => {
    const on = e.target.checked;
    setShowDebugPanel(on);
    localStorage.setItem(SHOW_DEBUG_KEY, on.toString());
    notifyDebugChanged();
  };
  const handleHoverTool = (toolKey, lsKey, checked) => {
    localStorage.setItem(lsKey, checked.toString());
    setHoverTools(prev => ({ ...prev, [toolKey]: checked }));
    syncHoverState();
    notifyDebugChanged();
  };
  const handleCssVarsMode = (value) => {
    setCssVarsMode(value);
    localStorage.setItem(CSS_VARS_MODE_KEY, value);
  };
  const handleOverlayTool = (toolKey, lsKey, checked) => {
    localStorage.setItem(lsKey, checked.toString());
    setOverlayTools(prev => ({ ...prev, [toolKey]: checked }));
    syncOverlayTool(toolKey, checked);
    notifyDebugChanged();
  };
  const handleRestoreDefaults = () => {
    Object.keys(localStorage)
      .filter(k => k.startsWith('pmd-') || k.startsWith('dawn.') || k.startsWith('activityTracker.'))
      .forEach(k => localStorage.removeItem(k));
    cleanupAllOverlays();
    notifyDebugChanged();
    window.location.reload();
  };

  return (
    <>
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <Settings size={18} />
          <h1>Preferences</h1>
        </div>
      </div>

      <div>

        {/* â”€â”€ This Session â”€â”€ */}
        <SectionHeader title="This Session" />
        <div className="stui-stat-row">
          <div className="stui-stat-item">
            <span className="stui-stat-label">Browser</span>
            <span className="stui-stat-value">{session.browser}</span>
          </div>
          <div className="stui-stat-item">
            <span className="stui-stat-label">Operating System</span>
            <span className="stui-stat-value">{session.os}</span>
          </div>
          <div className="stui-stat-item">
            <span className="stui-stat-label">Device</span>
            <span className="stui-stat-value">{session.device}</span>
          </div>
          <div className="stui-stat-item">
            <span className="stui-stat-label">User</span>
            <span className="stui-stat-value">{currentUser?.displayName || currentUser?.email || 'â€”'}</span>
          </div>
        </div>

        {/* â”€â”€ General â”€â”€ */}
        <SectionHeader title="General" />
        <div className="stui-row-setting">
          <div className="stui-row-setting-label">
            <div className="stui-label"><span className="stui-badge-construction">Under Construction</span> Default Database</div>
            <div className="stui-sublabel">Database to open on login</div>
          </div>
          <select value={defaultDb} onChange={e => handleDefaultDb(e.target.value)} className="stui-select">
            {databases.map(db => {
              const name = db.displayName || db.name;
              const slug = name?.toLowerCase();
              return <option key={db.id} value={slug}>{name}</option>;
            })}
          </select>
        </div>

        {/* â”€â”€ Display â”€â”€ */}
        <SectionHeader title="Display" />
        <div className="stui-row-setting">
          <div className="stui-row-setting-label">
            <div className="stui-label"><span className="stui-badge-construction">Under Construction</span> Color Theme</div>
            <div className="stui-sublabel">Changes the color scheme of the entire application</div>
          </div>
          <div className="stui-pill-group">
            {THEME_OPTIONS.map(t => (
              <button key={t.value} className={`stui-pill${theme === t.value ? ' selected' : ''}`} onClick={() => handleTheme(t.value)}>{t.label}</button>
            ))}
          </div>
        </div>
        <div className="stui-row-setting">
          <div className="stui-row-setting-label">
            <div className="stui-label"><span className="stui-badge-construction">Under Construction</span> UI Zoom Level</div>
            <div className="stui-sublabel">Scales the DAWN interface (this machine only)</div>
          </div>
          <div className="stui-pill-group">
            {ZOOM_OPTIONS.map(z => (
              <button key={z} className={`stui-pill${zoom === z ? ' selected' : ''}`} onClick={() => handleZoom(z)}>{z}</button>
            ))}
          </div>
        </div>
        <div className="stui-label-note">Zoom affects the DAWN application only, not your browser chrome or other tabs.</div>

        {/* â”€â”€ Downloads â”€â”€ */}
        <SectionHeader title="Downloads" />
        <div className="stui-row-setting">
          <div className="stui-row-setting-label">
            <div className="stui-label"><span className="stui-badge-construction">Under Construction</span> Max Concurrent Downloads</div>
            <div className="stui-sublabel">Additional downloads queue until a slot opens</div>
          </div>
          <div className="stui-pill-group">
            {CONCURRENCY_OPTIONS.map(opt => (
              <button key={opt.value} className={`stui-pill${concurrency === opt.value ? ' selected' : ''}`} onClick={() => handleConcurrency(opt.value)}>{opt.label}</button>
            ))}
          </div>
        </div>

        <SectionHeader title="Download Locations" />

        {!fsSupported && (
          <div>
            Custom download locations require Chrome or Edge. Other browsers will always show the system save dialog.
          </div>
        )}

        {LOCATION_KEYS.map(({ key, label, sub }) => (
          <div key={key} className="stui-row-setting">
            <div className="stui-row-setting-label">
              <div className="stui-label"><span className="stui-badge-construction">Under Construction</span> {label}</div>
              <div className="stui-sublabel">{sub}</div>
            </div>
            <div className="stui-row-setting-controls">
              {locationLabels[key] ? (
                <>
                  <span>{locationLabels[key]}</span>
                  <button className="stui-btn stui-btn-primary" onClick={() => handleBrowse(key)} disabled={!fsSupported}><FolderOpen size={13} /> Change</button>
                  <button className="stui-btn stui-btn-secondary" onClick={() => handleClearLocation(key)} title="Clear stored location"><X size={12} /></button>
                </>
              ) : (
                <>
                  <span>{key === 'app-default' ? 'System default (browser downloads)' : 'Use Application Default'}</span>
                  <button className="stui-btn stui-btn-primary" onClick={() => handleBrowse(key)} disabled={!fsSupported}><FolderOpen size={13} /> Browse...</button>
                </>
              )}
            </div>
          </div>
        ))}

        {/* â”€â”€ Language & Locale â”€â”€ */}
        <SectionHeader title="Language & Locale" />
        <div className="stui-row-setting">
          <div className="stui-row-setting-label">
            <div className="stui-label"><span className="stui-badge-construction">Under Construction</span> Language</div>
            <div className="stui-sublabel">Application display language</div>
          </div>
          <select value={language} onChange={e => handleLocale(setLanguage, 'pmd-locale.language')(e.target.value)} className="stui-select">
            {LANGUAGE_OPTIONS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </div>
        <div className="stui-row-setting">
          <div className="stui-row-setting-label">
            <div className="stui-label"><span className="stui-badge-construction">Under Construction</span> Date Format</div>
            <div className="stui-sublabel">How dates are displayed throughout the app</div>
          </div>
          <div className="stui-pill-group">
            {DATE_FORMAT_OPTIONS.map(f => (
              <button key={f.value} className={`stui-pill${dateFormat === f.value ? ' selected' : ''}`} onClick={() => handleLocale(setDateFormat, 'pmd-locale.dateFormat')(f.value)}>{f.label}</button>
            ))}
          </div>
        </div>
        <div className="stui-row-setting">
          <div className="stui-row-setting-label">
            <div className="stui-label"><span className="stui-badge-construction">Under Construction</span> Time Format</div>
            <div className="stui-sublabel">12-hour or 24-hour clock display</div>
          </div>
          <div className="stui-pill-group">
            {TIME_FORMAT_OPTIONS.map(f => (
              <button key={f.value} className={`stui-pill${timeFormat === f.value ? ' selected' : ''}`} onClick={() => handleLocale(setTimeFormat, 'pmd-locale.timeFormat')(f.value)}>{f.label}</button>
            ))}
          </div>
        </div>
        <div className="stui-row-setting">
          <div className="stui-row-setting-label">
            <div className="stui-label"><span className="stui-badge-construction">Under Construction</span> Timezone</div>
            <div className="stui-sublabel">Used for time display and scheduling</div>
          </div>
          <select value={timezone} onChange={e => handleLocale(setTimezone, 'pmd-locale.timezone')(e.target.value)} className="stui-select">
            {TIMEZONE_OPTIONS.map(tz => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </div>
        <div className="stui-row-setting">
          <div className="stui-row-setting-label">
            <div className="stui-label"><span className="stui-badge-construction">Under Construction</span> Number Format</div>
            <div className="stui-sublabel">Decimal and thousands separator style</div>
          </div>
          <div className="stui-pill-group">
            {NUMBER_FORMAT_OPTIONS.map(f => (
              <button key={f.value} className={`stui-pill${numberFormat === f.value ? ' selected' : ''}`} onClick={() => handleLocale(setNumberFormat, 'pmd-locale.numberFormat')(f.value)}>{f.label}</button>
            ))}
          </div>
        </div>
        <div className="stui-label-note">Language and locale settings are stored locally. Full localization support is coming in a future release.</div>

        {/* â”€â”€ Following â”€â”€ */}
        <SectionHeader title="Following" underConstruction />

        {/* â”€â”€ Subscriptions â”€â”€ */}
        <SectionHeader title="Subscriptions" />
        {SUBSCRIPTION_TOGGLES.map(t => (
          <ToggleRow
            key={t.key}
            label={t.label}
            sub={t.sub}
            checked={subs[t.key]}
            onChange={e => handleSub(t.key, e.target.checked)}
            badge
          />
        ))}
        <div className="stui-label-note">Subscription delivery requires backend configuration. Preferences are saved locally until that is available.</div>

        {/* â”€â”€ Security â”€â”€ */}
        <SectionHeader title="Security" />
        <div className="stui-row-setting">
          <div className="stui-row-setting-label">
            <div className="stui-label"><span className="stui-badge-construction">Under Construction</span> Session Timeout</div>
            <div className="stui-sublabel">Auto-logout after inactivity (enforcement requires backend support)</div>
          </div>
          <div className="stui-pill-group">
            {SESSION_TIMEOUT_OPTIONS.map(opt => (
              <button key={opt.value} className={`stui-pill${sessionTimeout === opt.value ? ' selected' : ''}`} onClick={() => handleSessionTimeout(opt.value)}>{opt.label}</button>
            ))}
          </div>
        </div>
        <ToggleRow
          label="Restrict Screen Capture & Printing"
          sub="Disables text selection and blocks print/PDF export. Cannot prevent OS-level screenshots."
          checked={screenRestrict}
          onChange={handleScreenRestrict}
          badge
        />
        {screenRestrict && (
          <div>
            Screen capture restriction is active â€” text selection and printing are disabled.
          </div>
        )}
        {/* â”€â”€ Autofill â”€â”€ */}
        <SectionHeader title="Autofill" />
        <ToggleRow
          label="Remember Username on Login"
          sub="Allows the browser to autofill your username on the login form"
          checked={autofillUsername}
          onChange={handleAutofillUsername}
          badge
        />
        <ToggleRow
          label="Browser Autofill on Forms"
          sub="Allows the browser to suggest previously entered values in application forms"
          checked={autofillForms}
          onChange={handleAutofillForms}
          badge
        />

        {/* â”€â”€ Debug â”€â”€ */}
        <SectionHeader title="Debug - General" />
        <ToggleRow
          label="Show Debug Tools in Footer"
          sub="Enables the debug button in the footer. Turn off here to remove it."
          checked={showDebugPanel}
          onChange={handleShowDebugPanel}
          badge
        />
        {showDebugPanel && (
          <div className="stui-label-note">
            Debug toolbar is active in the footer. Navigate to any page and use the debug button or Shift+F12 to access tools.
          </div>
        )}
        <div className="stui-subsection-divider" />
        <ToggleRow
          label="Enable Debug Logging"
          sub="Verbose console output for activity tracking, exports, and download resolution"
          checked={debugMode}
          onChange={handleDebugToggle}
          badge
        />


        {/* â”€â”€ Debug - Hover Inspectors â”€â”€ */}
        <SectionHeader title="Debug - Hover Inspectors" />
        <div className="stui-label-note">Active on all pages until disabled. Use the footer debug button or Shift+F12 to access from any page.</div>
        {DEBUG_HOVER_TOOLS.map(t => (
          <ToggleRow
            key={t.key}
            label={t.label}
            sub={t.sub}
            checked={hoverTools[t.key]}
            onChange={e => handleHoverTool(t.key, t.lsKey, e.target.checked)}
            badge
          />
        ))}
        <div className="stui-row-setting">
          <div className="stui-row-setting-label">
            <div className="stui-label"><span className="stui-badge-construction">Under Construction</span> CSS Variable Inspector Mode</div>
            <div className="stui-sublabel">How the inspector finds variables on the hovered element</div>
          </div>
          <div className="stui-pill-group">
            <button className={`stui-pill${cssVarsMode === 'used' ? ' selected' : ''}`} onClick={() => handleCssVarsMode('used')}>Used by element</button>
            <button className={`stui-pill${cssVarsMode === 'scope' ? ' selected' : ''}`} onClick={() => handleCssVarsMode('scope')}>In scope (all)</button>
          </div>
        </div>

        {/* â”€â”€ Debug - Page Overlays â”€â”€ */}
        <SectionHeader title="Debug - Page Overlays" />
        <div className="stui-label-note">Visual overlays applied to the entire app. Active on all pages until disabled.</div>
        {DEBUG_OVERLAY_TOOLS.map(t => (
          <ToggleRow
            key={t.key}
            label={t.label}
            sub={t.sub}
            checked={overlayTools[t.key]}
            onChange={e => handleOverlayTool(t.key, t.lsKey, e.target.checked)}
            badge
          />
        ))}

        {/* â”€â”€ Actions â”€â”€ */}
        <SectionHeader title="Actions" />
        <div className="stui-row-setting">
          <div className="stui-row-setting-label">
            <div className="stui-label"><span className="stui-badge-construction">Under Construction</span> Activity Log</div>
            <div className="stui-sublabel">View user activity and change history</div>
          </div>
          <button className="stui-btn stui-btn-primary" disabled><Activity size={13} /> View Activity Log</button>
        </div>
        <div className="stui-row-setting">
          <div className="stui-row-setting-label">
            <div className="stui-label"><span className="stui-badge-construction">Under Construction</span> Event Log</div>
            <div className="stui-sublabel">View system event history</div>
          </div>
          <button className="stui-btn stui-btn-primary" disabled><List size={13} /> View Event Log</button>
        </div>
        <div className="stui-row-setting">
          <div className="stui-row-setting-label">
            <div className="stui-label"><span className="stui-badge-construction">Under Construction</span> Active Sessions</div>
            <div className="stui-sublabel">View currently active user sessions</div>
          </div>
          <button className="stui-btn stui-btn-primary" disabled><Users size={13} /> Active Sessions</button>
        </div>

        {/* â”€â”€ Restore Defaults â”€â”€ */}
        <div className="stui-row-setting">
          <div className="stui-row-setting-label">
            <div className="stui-label">Restore Defaults</div>
            <div className="stui-sublabel">Reset all preferences on this machine to their default values</div>
          </div>
          <div className="stui-row-setting-controls">
            {!restoreConfirm ? (
              <button className="stui-btn stui-btn-primary" onClick={() => setRestoreConfirm(true)}>
                <RefreshCw size={13} /> Restore Defaults
              </button>
            ) : (
              <>
                <AlertTriangle size={14} />
                <span className="stui-sublabel">Reset all preferences. Continue?</span>
                <button className="stui-btn stui-btn-primary" onClick={handleRestoreDefaults}>Yes, reset</button>
                <button className="stui-btn stui-btn-secondary" onClick={() => setRestoreConfirm(false)}>Cancel</button>
              </>
            )}
          </div>
        </div>

      </div>
    </>
  );
};

export default PreferencesPage;
